import type { ReactNode } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

/**
 * Responsive list. md+: real table (horizontal scroll if needed). Below md: one
 * card per row. `mobile` decides where a column lands on the card:
 *   primary   title line (first primary), extra primaries follow on the title row
 *   status    right of the title line (put the StatusPill column here)
 *   secondary key/value list under the title
 *   meta      muted 11px line at the bottom
 *   hidden    table only
 * Default is "secondary".
 *
 *   <DataTable rows={onts} rowKey={(o) => o.id} columns={[
 *     { key: "serial", header: "Serial", cell: (o) => <Link ...>{o.serial}</Link>, mobile: "primary", mono: true },
 *     { key: "state", header: "State", cell: (o) => <StatusPill .../>, mobile: "status" },
 *     { key: "olt", header: "OLT", cell: (o) => o.olt_name },
 *     { key: "seen", header: "Last seen", cell: (o) => fmt(o.last_seen), mobile: "meta", mono: true },
 *   ]} />
 */
export type Column<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  mobile?: "primary" | "status" | "secondary" | "meta" | "hidden";
  /** Data column: Plex Mono + tabular numerals. */
  mono?: boolean;
  /** Extra classes for the desktop th/td (e.g. "text-right"). */
  className?: string;
};

export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  empty,
  onRowClick,
  className,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T, i: number) => string | number;
  /** Defaults to the translated "no rows" line. */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  className?: string;
}) {
  const { t } = useI18n();
  if (rows.length === 0)
    return <div className="py-6 text-center text-[13px] text-slate-500">{empty ?? t("common.empty")}</div>;
  const primary = columns.filter((c) => c.mobile === "primary");
  const status = columns.filter((c) => c.mobile === "status");
  const secondary = columns.filter((c) => !c.mobile || c.mobile === "secondary");
  const meta = columns.filter((c) => c.mobile === "meta");
  const rowCls = onRowClick ? "cursor-pointer hover:bg-slate-800/40" : "";
  return (
    <div className={cn("min-w-0", className)}>
      {/* md+ table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={c.className}>{c.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={rowKey(r, i)} className={rowCls} onClick={onRowClick && (() => onRowClick(r))}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={cn(c.mono && "data", c.className)}>{c.cell(r)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {/* <md cards */}
      <ul className="flex flex-col gap-2 md:hidden">
        {rows.map((r, i) => (
          <li
            key={rowKey(r, i)}
            className={cn("min-w-0 rounded-md border border-slate-800 bg-card p-3", rowCls)}
            onClick={onRowClick && (() => onRowClick(r))}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                {primary.map((c) => (
                  <span key={c.key} className={cn("truncate font-medium text-slate-100", c.mono && "data")}>
                    {c.cell(r)}
                  </span>
                ))}
              </div>
              {status.map((c) => (
                <span key={c.key} className="shrink-0">{c.cell(r)}</span>
              ))}
            </div>
            {secondary.length > 0 && (
              <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13px]">
                {secondary.map((c) => (
                  <div key={c.key} className="contents">
                    <dt className="text-slate-500">{c.header}</dt>
                    <dd className={cn("min-w-0 truncate text-slate-200", c.mono && "data")}>{c.cell(r)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {meta.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
                {meta.map((c) => (
                  <span key={c.key} className={cn(c.mono && "data")}>{c.cell(r)}</span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
